import { Badge } from "@/components/ui/badge"
import { ProcessedTestData } from '../utils/processTestData'
import { convertRatingToIndex } from '@/app/features/reports/ratingHelpers'
import Rating, { RatingMixedScore } from '@/components/raytd/rating-tool'
import { getMixedRatingLabel, getLabelTypesForRatingTypes } from '../ratingHelpers'
import { SectionText } from "@/app/features/reports/components/section-text"

type TestDistributionSectionProps = {
    filteredData: ProcessedTestData | null;
    unfilteredData: ProcessedTestData | null;
    filtersActive: boolean;
}

export function TestDistributionSection({ filteredData, unfilteredData, filtersActive }: TestDistributionSectionProps) {
    if (!unfilteredData) {
        return (
            <section className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-medium">Rating Distribution</h2>
                    <Badge variant="secondary">No Data Available</Badge>
                </div>
                <p>No test distribution data available. Please ensure that test data is properly loaded.</p>
            </section>
        );
    }

    return (
        <section className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">Rating Distribution</h2>
                {filtersActive && <Badge variant="secondary">Filter Applied</Badge>}
            </div>

            <SectionText
                position="summary-test-distribution-before"
            />

            <RatingTestDistributionGrid filteredData={filteredData} unfilteredData={unfilteredData} filtersActive={filtersActive} />

            <SectionText
                position="summary-test-distribution-after"
            />
        </section>
    )
}

function getBgColor(scaleType: string | undefined) {

    switch (scaleType) {
        case 'compliance':
        case 'compliance_b':
            return 'bg-compliance-500';
        case 'condition':
            return 'bg-condition-500';
        case 'generic':
            return 'bg-gray-400';
        default:
            return 'bg-pink-400';
    }
};

type GroupData = {
    count: number;
    scaleTypes: Record<string, number>;
}

type RatingGroup = {
    score: number | undefined;
    rating: keyof ProcessedTestData['testDistribution'];
    labels: string[];
}

function RatingTestDistributionGrid({ filteredData, unfilteredData, filtersActive }: { filteredData: ProcessedTestData | null, unfilteredData: ProcessedTestData, filtersActive: boolean }) {
    if (!unfilteredData) return null;

    const activeData = filteredData || unfilteredData;
    const ratingTypes = activeData.ratingTypes || [];
    const totalTests = activeData.totalTests || 0;
    const totalWaffleTests = unfilteredData.totalTests || 0;
    const createEmptyGroupData = (): GroupData => ({ count: 0, scaleTypes: {} });

    const baseGroups: Omit<RatingGroup, 'labels'>[] = [
        { score: 1, rating: 'very_poor' },
        { score: 2, rating: 'poor' },
        { score: 3, rating: 'fair' },
        { score: 4, rating: 'good' },
        { score: 5, rating: 'very_good' },
        { score: undefined, rating: 'no_rating' },
    ];

    const groups: RatingGroup[] = baseGroups.map(group => ({
        ...group,
        labels: getMixedRatingLabel(group.rating as any, getLabelTypesForRatingTypes(ratingTypes as any))
    }))

    return (
        <div className="print-rating-distribution grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 items-end auto-rows-min">
            {groups.map(group => {
                const unfilteredGroupData = unfilteredData.testDistribution[group.rating] || createEmptyGroupData();
                const filteredGroupData = filteredData?.testDistribution[group.rating] || createEmptyGroupData();
                const unfilteredGroupCount = unfilteredGroupData.count || 0;
                const groupCount = filteredData ? filteredGroupData.count : unfilteredGroupCount;

                return (
                    <RatingCard
                        key={group.rating}
                        group={group}
                        ratingTypes={ratingTypes}
                        groupCount={groupCount}
                        unfilteredGroupData={unfilteredGroupData}
                        filteredGroupData={filteredGroupData}
                        unfilteredGroupCount={unfilteredGroupCount}
                        totalTests={totalTests}
                        totalWaffleTests={totalWaffleTests}
                        filtersActive={filtersActive}
                    />
                );
            })}
        </div>
    )
}

const RatingCard = ({
    group,
    ratingTypes,
    groupCount,
    unfilteredGroupData,
    filteredGroupData,
    unfilteredGroupCount,
    totalTests,
    totalWaffleTests,
    filtersActive
}: {
    group: RatingGroup,
    ratingTypes: string[],
    groupCount: number,
    unfilteredGroupData: GroupData,
    filteredGroupData: GroupData,
    unfilteredGroupCount: number,
    totalTests: number,
    totalWaffleTests: number,
    filtersActive: boolean
}) => {
    const percentage = totalTests > 0 ? ((groupCount / totalTests) * 100).toFixed(0) : '0';

    return (
        <div className="flex flex-col items-center bg-zinc-100 p-4 space-y-6 h-full">

            <div className="space-y-2 text-center">
                <div className="flex flex-col items-center space-y-3">
                    <h3 className="font-medium h-16 print:h-auto justify-center flex flex-col text-base">
                        {group.labels.map((label, index) => (
                            <div key={index} className={index === 2 ? 'text-base' : undefined}>{label}</div>
                        ))}
                    </h3>

                    <RatingMixedScore
                        width={200}
                        height={40}
                        type={ratingTypes as any}
                        score={group.score}

                    />

                    <div className="flex flex-col items-center">
                        <p className="text-xs font-medium py-1">Tests</p>
                        <p className="text-xl font-bold tracking-wider">
                            {groupCount}
                        </p>
                        <div className="text-base font-light text-accent-foreground">
                            ({percentage}%)
                        </div>
                    </div>

                </div>
            </div>

            <TestWaffle2
                unfilteredGroupData={unfilteredGroupData}
                filteredGroupData={filteredGroupData}
                unfilteredGroupCount={unfilteredGroupCount}
                    totalTests={totalWaffleTests}
                filtersActive={filtersActive}
            />

        </div>
    );
}

const TestWaffle2 = ({
    unfilteredGroupData,
    filteredGroupData,
    unfilteredGroupCount,
    totalTests,
    filtersActive
}: {
    unfilteredGroupData: GroupData;
    filteredGroupData: GroupData;
    unfilteredGroupCount: number;
    totalTests: number;
    filtersActive: boolean;
}) => {

    const requiredRows = Math.ceil(unfilteredGroupCount / 10);
    const totalRows = Math.max(requiredRows, 1);

    const testToScaleType = Object.entries(unfilteredGroupData.scaleTypes).reduce((acc: string[], [scaleType, count]) => {
        return [...acc, ...Array(count).fill(scaleType)];
    }, []);

    return (
        <div
            className="w-32 flex flex-col mb-4 h-full"
            // style={{ height: `${Math.max(8, totalRows * 3.2)}rem` }}
        >
            {/* Spacer div that takes up remaining space */}
            <div className="flex-grow" />

            {/* Grid container with no flex-grow */}
            <div className="grid grid-cols-10 gap-0.5">
                {Array.from({ length: totalRows * 10 }).map((_, index) => {
                    const row = Math.floor(index / 10);
                    const col = index % 10;
                    const adjustedIndex = ((totalRows - 1) - row) * 10 + col;

                    const isFilled = adjustedIndex < unfilteredGroupCount;
                    const scaleType = isFilled ? testToScaleType[adjustedIndex] : null;
                    const isFiltered = !filtersActive || (
                        scaleType &&
                        filteredGroupData.scaleTypes[scaleType] &&
                        filteredGroupData.scaleTypes[scaleType] > 0
                    );

                    return (
                        <div
                            key={index}
                            className={`aspect-square rounded-sm ${isFilled
                                    ? (isFiltered ? getBgColor(scaleType) : 'bg-zinc-200')
                                    : 'bg-transparent'
                                }`}
                        />
                    );
                })}
            </div>
        </div>
    );
}
